
import { CommentDetailProps } from "./commentDetail";
import { useTranslation } from "react-i18next";
import { AuthContext } from "core/contexts/auth/auth";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { useCopyToClipboard } from "core/hooks/copytoClipboard";
import { useAppDispatch, useAppSelector } from "core/store/reduxStore";
import { IconX, IconMaximize, IconMinimize } from "@tabler/icons-react";
import {
  setPreviewWidth,
  setSelectedRow,
} from "modules/flywheel/store/incident";

import {
  AlertSummaryContainer,
  TextContainer,
  CommentsContainer,
  PanelContainer,
  AccordionContainer,
} from "./alertSummary.styled";
import {
  AccordionSummary,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Accordion,
  AccordionDetails,
  Modal,
  CircularProgress,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AlertComments from "./alertComments";
import styled, { useTheme } from "styled-components";
import CustomMarkdown from "core/components/global/markdown/markdown";
import ChatInterface from "./ChatInterface";
import StreamMarkdownPage from "../searchSimilar/workFlowView";
import { useAppTheme } from "core/contexts/theme/theme";
import {
  fetchRecommendations,
  executePlaybook,
} from "modules/flywheel/services/incidentService";
import DynamicJsonViewer from "./playBookSummary";

// Add these styled components to your alertSummary.styled.ts file or create them inline
const PlaybookModal = styled(Modal)`
  .MuiPaper-root {
    border-radius: 1rem;
    border: none;
    background-color:white;
    color:black;
    box-shadow: 1.5px 3px 3px hsl(0deg 0% 0% / 0.45);
  }
`;

const PlaybookModalInnerContainer = styled(Paper)`
  width: max-content;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80%;
  max-width: 800px;
  max-height: 80vh;
  border: 2px solid #000;
  border-radius: 1rem;
`;

const PlaybookModalHeader = styled("div")`
  font-size: ${({ theme }) => theme.font.xl};
  display: flex;
  border-bottom: ${({ theme }) => theme.borders.headerBorder};
  color: black;
  padding: 0.75rem 1.25rem;
  position: relative;
  border-top-left-radius: 0.25rem;
  border-top-right-radius: 0.25rem;
  justify-content: space-between;
`;

const PlaybookModalBody = styled("div")`
  padding: 1.25em;
  max-height: 60vh;
  overflow-y: auto;

  .recessed {
    padding: 0.375rem 0.75rem;
    margin-top: 0.25rem;
    box-shadow: ${({ theme }) => theme.shadows.boxShadowInset};
    border-radius: ${({ theme }) => theme.borders.borderRadius};
    color:black;
    font-size: 100%;
  }
`;

const PlaybookModalButtonContainer = styled("div")`
  display: flex;
  justify-content: end;
  padding: 0 1.25rem 1.25rem;
`;

export interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
}

export interface IRelatedAlerts {
  id: string;
  description?: string;
  createdDate?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number | string;
  value: number | string;
}

interface AlertDetailsAccordionProps {
  assigned?: string[];
  tags?: any[];
  org?: string;
  lastModified: string;
  createdDate: string;
  incidentState: any;
  selectedIncident:any;
}

interface PlaybookStep {
  number: number;
  description: string;
}

interface Playbook {
  name: string;
  id: string;
  score: number;
  reasoning: string;
  automation: string;
  missing_data: string[];
  execution_command: string;
  required_parameters: string[];
  estimated_duration_minutes: number;
  steps: PlaybookStep[];
}

interface PlaybookExecutionState {
  [playbookName: string]: {
    isExecuting: boolean;
    hasExecuted: boolean;
    response?: string;
  };
}

const renderPlaybookSkeleton = (apptheme) => {
  return Array.from({ length: 3 }).map((_, index) => (
    <Accordion
      key={`skeleton-${index}`}
      elevation={0}
      style={{ borderRadius: apptheme.borders.borderRadius }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Skeleton
          variant="text"
          width={`${60 + index * 10}%`}
          height={24}
          animation="wave"
        />
      </AccordionSummary>
      <AccordionDetails>
        <div className="flex flex-col space-y-2">
          <Skeleton
            variant="rectangular"
            width={100}
            height={36}
            style={{ borderRadius: "18px" }}
            animation="wave"
          />
          <div className="mt-4">
            <Skeleton variant="text" width="90%" height={20} animation="wave" />
            <Skeleton variant="text" width="85%" height={20} animation="wave" />
            <Skeleton variant="text" width="80%" height={20} animation="wave" />
          </div>
        </div>
      </AccordionDetails>
    </Accordion>
  ));
};

export function AlertDetailsAccordion({
  assigned,
  tags,
  org,
  lastModified,
  createdDate,
  incidentState,
  selectedIncident
}: AlertDetailsAccordionProps) {
  const { apptheme } = useAppTheme();
  const [playbooks, setPlaybooks] = useState<Playbook[]>([]);
  const [playbookExecutionStates, setPlaybookExecutionStates] =
    useState<PlaybookExecutionState>({});
  const [selectedPlaybookResponse, setSelectedPlaybookResponse] =
    useState<string>("");
  const [isJsonSummary, setIsJsonSummary] = useState<boolean>(false);
  const [isLoadingPlaybooks, setIsLoadingPlaybooks] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { checkAuthToken } = useContext(AuthContext);

  const asyncFetchRecommendations = async () => {
    setIsLoadingPlaybooks(true);
    const amsBearerToken = await checkAuthToken();

    const selectedEvent = incidentState.pageEvents.find(
      (e) => e.id === incidentState.selectedRow
    );

    try {
      let response = await fetchRecommendations(amsBearerToken, {
        user_input: JSON.stringify(selectedEvent),
        conversationid: incidentState.selectedId,
      });

      let jsonResp;
      try {
        jsonResp = typeof response.response === 'string' ? JSON.parse(response.response) : response.response;
      } catch (e) {
        console.error('Failed to parse response:', e, response.response);
        jsonResp = {};
      }

      if ("recommendations" in jsonResp) {
        const recommendations = jsonResp.recommendations;
        setPlaybooks(recommendations);
      }
    } catch (error) {
      console.error("Error fetching recommended playbooks:", error);
    } finally {
      setIsLoadingPlaybooks(false);
    }
  };

  useEffect(() => {
    setPlaybooks([]);
    setPlaybookExecutionStates({});
    setSelectedPlaybookResponse("");
    setIsLoadingPlaybooks(false);
    setIsModalOpen(false);
    asyncFetchRecommendations();
  }, [incidentState.selectedRow]);


  const asyncExecutePlaybook = async (playbook: Playbook) => {
    const amsBearerToken = await checkAuthToken();

    // Set the playbook to executing state
    setPlaybookExecutionStates((prev) => ({
      ...prev,
      [playbook.name]: {
        isExecuting: true,
        hasExecuted: false,
      },
    }));

    try {
      // Call the execution
      let response = await executePlaybook(amsBearerToken, {
        user_input: playbook.execution_command,
        conversationid: incidentState.selectedId,
      });

      // Update state with the response
      setPlaybookExecutionStates((prev) => ({
        ...prev,
        [playbook.name]: {
          isExecuting: false,
          hasExecuted: true,
          response: response.response || "Execution completed successfully.",
        },
      }));
    } catch (error) {
      // Update state to show error
      setPlaybookExecutionStates((prev) => ({
        ...prev,
        [playbook.name]: {
          isExecuting: false,
          hasExecuted: true,
          response: "Error: Failed to execute playbook. Please try again.",
        },
      }));
    }
  };

  const handleViewDetails = (playbook: Playbook) => {
    const executionState = playbookExecutionStates[playbook.name];
    if (executionState?.response) {
      setSelectedPlaybookResponse(executionState.response);
      setIsModalOpen(true);
    }
  };

  const renderPlaybookButton = (playbook: Playbook) => {
    const executionState = playbookExecutionStates[playbook.name];

    if (executionState?.isExecuting) {
      // Show loading state
      return (
        <button
          style={{ width: "min-content" }}
          className="inline-flex items-center px-4 py-2 bg-gray-400 text-white rounded-2xl cursor-not-allowed"
          disabled
        >
          <CircularProgress
            size={16}
            style={{ color: "white", marginRight: "8px" }}
          />
          Executing...
        </button>
      );
    } else if (executionState?.hasExecuted) {
      // Show view details button
      return (
        <button
          className="inline-flex items-center justify-center px-4 py-2 bg-white text-[#22C55E] rounded-md hover:bg-green-200 font-bold"
          style={{
            border: "3px solid #22C55E",
            borderRadius: "20px",
          }}
          onClick={() => handleViewDetails(playbook)}
        >
          <span className="text-black font-bold">View Details</span>
        </button>
      );
    } else {
      // Show execute button
      return (
        <button
          style={{ width: "min-content" }}
          className="inline-flex items-center px-4 py-2 bg-[#9334E9] text-white rounded-2xl"
          onClick={() => asyncExecutePlaybook(playbook)}
        >
          Execute
        </button>
      );
    }
  };
  function pythonObjectToJSON(str: string): any | null {
  try {
    // Convert Python literals to JSON equivalents
    let jsonLike = str
      .replace(/\bNone\b/g, 'null')   // None → null
      .replace(/\bTrue\b/g, 'true')   // True → true
      .replace(/\bFalse\b/g, 'false') // False → false
      .replace(/'/g, '"');            // single quotes → double quotes

    // Parse to JSON
    return JSON.parse(jsonLike);
  } catch (err) {
    console.error('Failed to convert Python-style object to JSON:', err);
    return null;
  }
}

function detectContentType(input: string): 'pythonObject' | 'markdown' {
  const trimmed = input.trim();

  // Quick check for Python-style object
  const isPythonObject =
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']'));

  // Further validation: check for common Python literals
  const pythonPattern = /[\{\[\}\'\:\,]|None|True|False/;
  if (isPythonObject && pythonPattern.test(trimmed)) {
    return 'pythonObject';
  }

  // Otherwise, treat as Markdown
  return 'markdown';
}
// Utility to check if a string is a Python-style object (dict/list/tuple/True/False/None)
function isPythonStyleObject(str: string): boolean {
  if (!str || typeof str !== 'string') return false;
  // Heuristic: contains single quotes, Python booleans, None, or tuple/list/dict patterns
  const hasPythonQuotes = /'[^']*':/.test(str);
  const hasPythonBools = /\b(True|False|None)\b/.test(str);
  const hasTuple = /\([^()]+, [^()]+\)/.test(str);
  const hasDict = /\{[^}]+: [^}]+\}/.test(str);
  return hasPythonQuotes || hasPythonBools || hasTuple || hasDict;
}

function parsePythonObjectString(str: string): any | null {
  try {
    // 1. Normalize Python literals to JSON
    let normalized = str
      .replace(/\bNone\b/g, 'null')
      .replace(/\bTrue\b/g, 'true')
      .replace(/\bFalse\b/g, 'false');

    // 2. Convert tuples (...) to arrays [...] iteratively
    //    Simple iterative approach until no tuples remain
    while (/\([^()]*\)/.test(normalized)) {
      normalized = normalized.replace(/\(([^()]*?)\)/g, '[$1]');
    }

    // 3. Replace single quotes with double quotes carefully
    //    Avoid replacing inside already double-quoted strings
    normalized = normalized.replace(/'/g, '"');

    // 4. Wrap with braces if the top-level object looks like key-value pairs without {}
    const trimmed = normalized.trim();
    const topLevelWrapped =
      trimmed.startsWith('{') || trimmed.startsWith('[')
        ? trimmed
        : `{${trimmed}}`;

    // 5. Parse JSON
    return JSON.parse(topLevelWrapped);
  } catch (err) {
    console.error('Failed to parse Python-style object string:', err);
    return null;
  }
}

  return (
    <>
      <AccordionContainer className="flex flex-col w-full space-y-2">
        <Accordion
          className=""
          elevation={0}
          style={{
            backgroundColor:
              apptheme?.name === "light-theme" ? "#F9FAFB" : "#1E1E1E",
            borderRadius: "8px",
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1-content"
            id="panel1-header"
            style={{
              backgroundColor:
                apptheme?.name === "light-theme" ? "#F9FAFB" : "#1E1E1E",
              borderRadius: "8px",
            }}
          >
            <Typography className="font-medium">Alert Details</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer>
              <Table aria-label="alert-details" size="small">
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium w-1/3">
                      Finding Name
                    </TableCell>
                    <TableCell>{selectedIncident.findingName}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium w-1/3">AWS Org</TableCell>
                    <TableCell>{org || "-"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">
                      Assigned User(s)
                    </TableCell>
                    <TableCell>
                      {assigned?.length ? assigned.join(", ") : "-"}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Last Modified</TableCell>
                    <TableCell>{lastModified || "-"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Created</TableCell>
                    <TableCell>{createdDate || "-"}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        <Accordion
          className=""
          elevation={0}
          style={{
            backgroundColor:
              apptheme?.name === "light-theme" ? "#F9FAFB" : "#1E1E1E",
            borderRadius: "8px",
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel2-content"
            id="panel2-header"
            style={{
              backgroundColor:
                apptheme?.name === "light-theme" ? "#F9FAFB" : "#1E1E1E",
              borderRadius: "8px",
            }}
          >
            <Typography className="font-medium">Playbooks</Typography>
          </AccordionSummary>

          <AccordionDetails>
            <div className="flex flex-col space-y-3">
              {isLoadingPlaybooks ? (
                renderPlaybookSkeleton(apptheme)
              ) : playbooks.length > 0 ? (
                playbooks.map((playbook, index) => (
                  <Accordion
                    key={playbook.id || index}
                    elevation={0}
                    style={{ borderRadius: apptheme.borders.borderRadius }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>{playbook.name==="Test Incident Response"?"Automated Endpoint Threat Response":playbook.name}</Typography>
                    </AccordionSummary>

                    <AccordionDetails>
                      <div className="flex flex-col space-y-2">
                        {renderPlaybookButton(playbook)}

                        <ol className="list-[upper-alpha] pl-5 space-y-3 text-sm text-gray-600 mt-4">
                          {playbook.steps.map((step, stepIndex) => (
                            <li key={stepIndex}>{step.description}</li>
                          ))}
                        </ol>
                      </div>
                    </AccordionDetails>
                  </Accordion>
                ))
              ) : (
                <div className="text-center py-4">
                  <Typography
                    variant="body2"
                    style={{ color: apptheme.colors?.textColor || "#666" }}
                  >
                    No playbooks found for this alert.
                  </Typography>
                </div>
              )}
            </div>
          </AccordionDetails>
        </Accordion>
      </AccordionContainer>

      {/* Playbook Details Modal */}
      <PlaybookModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        aria-labelledby="playbook-details-title"
        aria-describedby="playbook-details-description"
      >
        <PlaybookModalInnerContainer elevation={2}>
          <PlaybookModalHeader>
            <div className="d-flex">
              <p className="ps-1">Playbook Execution Details</p>
            </div>
          </PlaybookModalHeader>
          <PlaybookModalBody>
            <div style={{ padding: "1rem" }}>
              {(() => {
                if (!selectedPlaybookResponse || selectedPlaybookResponse.trim() === "") {
                  return <Typography color="textSecondary">No playbook response available.</Typography>;
                }
                if (isPythonStyleObject(selectedPlaybookResponse)) {
                  const jsonData = parsePythonObjectString(selectedPlaybookResponse);
                  if (jsonData && typeof jsonData === 'object') {
                    return <DynamicJsonViewer data={jsonData} />;
                  } else {
                    // Python-style but failed to parse
                    return <CustomMarkdown content={selectedPlaybookResponse} />;
                  }
                }
                // Not Python-style: treat as markdown
                return <CustomMarkdown content={selectedPlaybookResponse} />;
              })()}
            </div>
          </PlaybookModalBody>
          <PlaybookModalButtonContainer>
            <Button
              variant="contained"
              onClick={() => setIsModalOpen(false)}
              style={{ backgroundColor: "#9334E9" }}
            >
              Close
            </Button>
          </PlaybookModalButtonContainer>
        </PlaybookModalInnerContainer>
      </PlaybookModal>
    </>
  );
}

export interface StandardMapping {
  Object(any);
}

export interface AlertSummarySchema {
  id: string;
  severity: string;
  status: string;
  classification: string;
  organization?: string;
  assignedUsers?: string[];
  description: string;
  standardMapping: any;
  awsBestPractice: string;
  terraformRemediation: string;
  awsCliRemediation: any;
  cloudformationTemplate: string;
  createdDate: string;
  lastModified: string;
  comments: CommentDetailProps[];
  tags?: any[];
  relatedAlerts?: any[];
}

export interface AlertSummaryProps {
  setOpenSideModal: Dispatch<SetStateAction<boolean>>;
  selectedId: string;
  model_details: any;
  user_input: any;
}

export const AlertSummary = ({
  setOpenSideModal,
  selectedId,
  model_details,
  user_input,
}: AlertSummaryProps) => {
  const theme = useTheme();
  const { afUser: user } = useContext(AuthContext);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const incidentState = useAppSelector((state) => state.incidents);
  const [isLoadingGeneral, setIsLoadingGeneral] = useState(true);
  const [userPrompt, setUserPrompt] = useState("");
  const { checkAuthToken } = useContext(AuthContext);
  const [tabValue, setTabValue] = useState({"actions": 0, "response": 0, "gap_analysis": ""});
  const [copiedText, copyToClipboard] = useCopyToClipboard();
  const [findingName, setFindingName] = useState("");
  const [isFullWidth, setIsFullWidth] = useState(false);
  const [prevWidth, setPrevWidth] = useState(50);

  // Find the selected incident from the pageEvents array
  const selectedIncident = incidentState.pageEvents.find(
    (event) => event.id === selectedId
  );

  useEffect(() => {
    // If we have the incident data, we're not loading
    if (selectedIncident) {
      setIsLoadingGeneral(false);
    }
  }, [selectedIncident]);

  useEffect(() => {}, [incidentState.selectedRow]);

  useEffect(() => {
    // If we have the incident data, we're not loading
    if (user_input && user_input !== "") {
      const temp: any = incidentState.pageEvents.filter((event) => {
        return event.id === incidentState.selectedRow;
      });

      if (temp.length > 0) {
        const localFindingName = temp[0].findingName;
        // setSelectedFindingName(localFindingName);
        setFindingName(localFindingName);
      }
    }
  }, [user_input, incidentState.selectedRow]);

  useEffect(() => {
    const copyText = async () => {
      if (copiedText) {
        const success = await copyToClipboard(copiedText);
        if (!success) {
          console.log(
            "Copy failed, no permission or clipboard support, update your browser permissions."
          );
        }
      }
    };

    copyText();
  }, [copiedText, copyToClipboard]);

  const [statusColor, setStatusColor] = useState(Object());

  const statusMapping = {
    Open: {
      bk: theme.colors.open_incident,
      text: theme.colors.textColor,
    },
    "In Progress": {
      bk: theme.colors.in_progress_incident,
      text: theme.colors.textColor,
    },
    Closed: {
      bk: theme.colors.closed_incident,
      text: theme.colors.textColor,
    },
    "Request Tuning": {
      bk: theme.colors.requested_incident,
      text: theme.colors.textColor,
    },
  };

  // Get the severity color based on severity string
  const getSeverityColor = (severity: string | undefined) => {
    if (!severity) return "#7b7881";

    // This is a safer approach than using theme.severity.range[severity]
    // which was causing the TypeScript error
    switch (severity) {
      case "SEVERITY.CRITICAL":
      case "Critical":
        return theme.severity.range["SEVERITY.CRITICAL"] || "#ff0000";
      case "SEVERITY.HIGH":
      case "High":
        return theme.severity.range["SEVERITY.HIGH"] || "#ff6600";
      case "SEVERITY.MEDIUM":
      case "Medium":
        return theme.severity.range["SEVERITY.MEDIUM"] || "#ffcc00";
      case "SEVERITY.LOW":
      case "Low":
        return theme.severity.range["SEVERITY.LOW"] || "#ffff00";
      case "SEVERITY.INFORMATIVE":
      case "Informative":
        return theme.severity.range["SEVERITY.INFORMATIVE"] || "#00ff00";
      default:
        return "#7b7881";
    }
  };

  useEffect(() => {
    if (selectedIncident?.status && statusMapping[selectedIncident?.status]) {
      setStatusColor({
        text: statusMapping[selectedIncident.status].text,
        bk: statusMapping[selectedIncident.status].bk,
      });
    }
  }, [selectedIncident?.status]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Generate a last modified date (if available, otherwise use current date)
  const getLastModifiedDate = () => {
    if (selectedIncident?.dateTime) {
      return formatDate(selectedIncident.dateTime);
    }
    return formatDate(new Date().toString());
  };

  // Use createdDate if available, otherwise use dateTime
  const getCreatedDate = () => {
    if (selectedIncident?.createdDate) {
      return formatDate(selectedIncident.createdDate);
    } else if (selectedIncident?.dateTime) {
      return formatDate(selectedIncident.dateTime);
    }
    return "";
  };

  // Toggle between full width and previous width
  const toggleFullWidth = ({ close }: { close?: boolean } = {}) => {
    try {
      if (close) {
        // Close the side panel and reset selected row
        setOpenSideModal(false);
        dispatch(setSelectedRow({ id: "", type: "" }));
      } else {
        if (isFullWidth) {
          // Return to previous width using Redux
          if (prevWidth > 10) {
            dispatch(setPreviewWidth(prevWidth));
          } else {
            dispatch(setPreviewWidth(30));
          }
        } else {
          // Save current width before going full width
          setPrevWidth(incidentState.previewWidth);

          // Set to maximum width using Redux
          dispatch(setPreviewWidth(100));
        }
        setIsFullWidth(!isFullWidth);
      }
    } catch (error) {
      console.error("Error in toggleFullWidth:", error);
    }
  };

  return (
    <AlertSummaryContainer container>
      <div className="d-flex justify-content-between w-100 align-items-center mb-2">
        <div className="d-flex align-items-center">
          <strong className="text-break">
            {t("INCIDENTS_PAGE.INCIDENT")}:{" "}
            {!isLoadingGeneral && selectedIncident?.id}
          </strong>
          {isLoadingGeneral && (
            <Skeleton
              className="ms-2"
              style={{ width: "55%", height: 36 }}
              animation="wave"
              variant="text"
            />
          )}
        </div>
        <div className="d-flex">
          <button
            onClick={() => toggleFullWidth()}
            style={{ color: theme.colors.textColor }}
            className={`p-2 rounded-full transition-colors`}
            title="Toggle width"
          >
            {isFullWidth ? (
              <IconMinimize size={18} stroke={1.5} />
            ) : (
              <IconMaximize size={18} stroke={1.5} />
            )}
          </button>
          <button
            onClick={() => toggleFullWidth({ close: true })}
            style={{ color: theme.colors.textColor }}
            className={`p-2 rounded-full transition-colors`}
            title="Close"
          >
            <IconX size={18} stroke={1.5} />
          </button>
        </div>
      </div>

      <AlertDetailsAccordion
        assigned={
          selectedIncident?.assignedto ? [selectedIncident.assignedto] : []
        }
        org={selectedIncident?.organization || selectedIncident?.org || ""}
        lastModified={getLastModifiedDate()}
        createdDate={getCreatedDate()}
        incidentState={incidentState}
        selectedIncident={selectedIncident}
      />

      {isLoadingGeneral ? (
        <>
          <div className="d-flex flex-row">
            <Skeleton
              style={{ width: 150, height: 52 }}
              animation="wave"
              variant="text"
            />
            <Skeleton
              className="ms-2"
              style={{ width: 150, height: 52 }}
              animation="wave"
              variant="text"
            />
          </div>
        </>
      ) : (
        <>
          <div className="d-flex flex-row">
            {selectedIncident ? (
              <>
                <div
                  style={{ backgroundColor: statusColor?.bk || "#7b7881" }}
                  className="mt-2 mb-2 ps-4 pe-4 pt-2 pb-2 rounded-3 shadow"
                >
                  <strong style={{ color: "rgb(39, 55, 70)", opacity: 0.9 }}>
                    {selectedIncident.status}
                  </strong>
                </div>
                <div
                  style={{
                    backgroundColor: getSeverityColor(
                      selectedIncident.severity
                    ),
                  }}
                  className="m-2 ms-2 ps-4 pe-4 pt-2 pb-2 rounded-3 shadow"
                >
                  <strong style={{ color: "rgb(39, 55, 70)", opacity: 0.9 }}>
                    {t("INCIDENTS_PAGE.SEVERITY")}: {selectedIncident.severity}
                  </strong>
                </div>
              </>
            ) : (
              <>
                <div
                  style={{ backgroundColor: "#7b7881" }}
                  className="mt-2 mb-2 ps-4 pe-4 pt-2 pb-2 rounded-3 shadow"
                >
                  <strong style={{ color: "rgb(39, 55, 70)", opacity: 0.9 }}>
                    Status
                  </strong>
                </div>
                <div
                  style={{ backgroundColor: "#7b7881" }}
                  className="m-2 ms-2 ps-4 pe-4 pt-2 pb-2 rounded-3 shadow"
                >
                  <strong style={{ color: "rgb(39, 55, 70)", opacity: 0.9 }}>
                    {t("INCIDENTS_PAGE.SEVERITY")}
                  </strong>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {selectedIncident?.findingName ? (
        <span className="d-flex flex-column">
          <strong className="mb-3" style={{ fontSize: theme.font.xl }}>
            Event:
          </strong>
          <p>
            {selectedIncident.findingName ||
              "Multiple Failed Login Attempts from Unusual Location"}
          </p>

          {selectedIncident.findingId && (
            <div style={{ fontSize: "0.9rem", marginTop: "10px" }}>
              <strong>Finding ID:</strong> {selectedIncident.findingId}
            </div>
          )}
        </span>
      ) : (
        isLoadingGeneral && (
          <>
            <Skeleton
              style={{ width: 150, height: 52 }}
              animation="wave"
              variant="text"
            />
            <Skeleton
              className="w-100"
              style={{ height: 150 }}
              animation="wave"
              variant="text"
            />
          </>
        )
      )}

      {/* AI Workflow section*/}
      {model_details.type == "workflow" && findingName !== "" && (
        <span
          style={{ overflow: "auto", wordWrap: "break-word", maxWidth: "100%" }}
        >
          <StreamMarkdownPage
            user_input={findingName}
            conversationId={selectedId}
          />
        </span>
      )}

      {/* Comments Section */}
    {/*   <CommentsContainer className="mb-2">
        <AlertComments
          t={t}
          theme={theme}
          initialComments={[]}
          alertId={selectedIncident?.id || ""}
          user={user}
        />
      </CommentsContainer> */}

      {/* Conversation Section */}
      <strong style={{ fontSize: theme.font.xl }}>
        {t("INCIDENTS_PAGE.CONVERSE_WITH_AI_FLYWHEEL")}:
      </strong>

      <ChatInterface
        userPrompt={userPrompt}
        setUserPrompt={setUserPrompt}
        theme={theme}
        conversationId={selectedId}
      />

      <TextContainer container spacing={2} className="d-flex flex-column">
        <strong style={{ fontSize: theme.font.xl }}>
          {t("INCIDENTS_PAGE.AI_DISCLAMER_TITLE")}:
        </strong>
        <strong style={{ fontSize: theme.font.large }}>
          {t("INCIDENTS_PAGE.PLEASE_NOTE")}:
        </strong>
        <strong className="mt-2">
          The information provided here has been generated by AI and, as such,
          may contain inaccuracies or &#39;hallucinations&#39;. We advise users
          to verify the accuracy of the information before relying on it for any
          critical decisions or actions.
        </strong>
      </TextContainer>
    </AlertSummaryContainer>
  );
};

export default AlertSummary;
